<template>
  <div class="d-flex p-relative gap-8 column wrapper">
    <div
      :class="{ focused: activeFocusClass, 'has-error': hasError, 'disable-input bg-cont-dim': disabled }"
      class="amount-wrapper d-flex p-relative"
      @click="onFocus"
      @focusin="onFocus"
    >
      <div class="w-100 d-flex text-bold bg-cont-white justify-center items-center gap-8 dir-ltr" style="direction: ltr">
        <template v-if="!disabled">
          <div :class="{ 'color-on-cont-dim': !filled, 'disable-input': disabled }" class="text-medium">ريال</div>
          <div :style="{ width: `calc(${calculateWidth}px + 70px)`, textAlign: 'center' }" class="input text-h32">
            <input
              ref="input"
              :placeholder="computedPlaceholder"
              :value="formattedAmountValue"
              inputmode="numeric"
              class="w-100 text-bold text-center"
              maxlength="13"
              @blur="onBlur"
              @focusout="onFocusOut"
              @input="updateValue"
            />
          </div>
        </template>
        <div v-else class="bg-cont-dim color-on-cont-muted w-100 d-flex justify-center items-center gap-4">
          <span class="text-c12"> ریال </span>
          <span class="text-h32">
            {{ formattedPlaceholder }}
          </span>
        </div>
      </div>
      <div v-if="hasError" class="p-relative">
        <Tooltip :text="error" :customClass="'error-icon p-absolute d-none d-md-flex h-100 items-center'" show="hover" tool-tip-class-name="tooltip-top-0">
          <span>
            <Icon name="error"></Icon>
          </span>
        </Tooltip>
      </div>
    </div>
    <div class="text-c12 text-medium color-on-cont-dim leaning-20 text-center" :class="{ 'color-on-cont-muted': disabled }">
      {{ hintText }}
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, getCurrentInstance, inject, nextTick, onBeforeUnmount, onMounted, ref, toRefs, watch, watchEffect } from 'vue';
import Icon from '@/components/base/icon/index.vue';
import Tooltip from '@/components/tooltip/index.vue';
import type { AmountInputProps, InjectFormType } from '@/types';
import { parseRialToPersianText, extractNumberFromString, removeCommas, seprateCommas, toEnNumber, testRegexOnlyNumbers } from '@/utils';
import { useRoute } from 'vue-router';
//ValidFunction
const MIN_AMOUNT = 10000;
const MAX_AMOUNT = 1000000000;
const props = withDefaults(defineProps<AmountInputProps>(), {
  amountType: 'OPTIONAL',
  modelValue: '',
  disabled: undefined,
  placeholder: '0',
});
const route = useRoute();
const { amountType, modelValue, placeholder, disabled } = toRefs(props);
const emit = defineEmits(['update:modelValue', 'low-amount']);

const amountValue = ref<string>(modelValue.value || '');
const focused = ref(false);
const input: any = ref(null);
const hasError = ref<boolean>(false);
const error = ref<string>('');
const form = inject<InjectFormType>('form');
const uid = getCurrentInstance()?.uid as number;
const persianValue = ref<string | null>(null);
const defaultAmount = ref<string | null>(null);

const defaultHintText = 'مبلغ مورد نظر را وارد کنید';
const validHintText = `حداقل ${seprateCommas(MIN_AMOUNT)} ریال وارد کنید`;
const hintText = ref<string | null>(defaultHintText);

watchEffect(() => {
  hintText.value = Number(placeholder.value) === 0 ? defaultHintText : persianValue.value;
  if (amountType.value === 'FIX' && placeholder.value && Number(placeholder.value) > 0) {
    amountValue.value = placeholder.value;
    persianValue.value = parseRialToPersianText(placeholder.value);
  }
});
watchEffect(() => {
  hintText.value = Number(amountValue.value) === 0 ? defaultHintText : Number(amountValue.value) > 0 && Number(amountValue.value) < MIN_AMOUNT ? validHintText : persianValue.value;
});

watchEffect(() => {
  defaultAmount.value = route.query.amount as string;
  !!route.query.amount && (persianValue.value = parseRialToPersianText(toEnNumber(defaultAmount.value)));
});

onMounted(() => {
  form && form.bind({ validate, uid });
});

onBeforeUnmount(() => {
  form && form.unbind(uid);
});

const filled = computed(() => {
  return focused.value || amountValue.value;
});

const calculateWidth = computed(() => {
  return amountValue.value.length * 18;
});

const activeFocusClass = computed<boolean>(() => {
  return focused.value;
});

const formattedAmountValue = computed(() => {
  const amount = Number(removeCommas(amountValue.value));
  return amount > MAX_AMOUNT ? amountValue.value : seprateCommas(amountValue.value);
});
const formattedPlaceholder = computed(() => !!placeholder.value && seprateCommas(placeholder.value));

const computedPlaceholder = computed(() => (focused.value ? '' : placeholder.value || ''));

function updateValue(event: Event) {
  const target = event.target as HTMLInputElement;
  const value = removeCommas(target.value);

  const englishNumbers = toEnNumber(value);
  if (testRegexOnlyNumbers(englishNumbers)) {
    target.value = seprateCommas(amountValue.value);
    return;
  }

  // Prevent values above MAX_AMOUNT
  if (Number(englishNumbers) > MAX_AMOUNT) {
    target.value = seprateCommas(amountValue.value);
    return;
  }

  amountValue.value = englishNumbers;
  target.value = Number(englishNumbers) > MAX_AMOUNT && Number(englishNumbers) !== MAX_AMOUNT ? englishNumbers : seprateCommas(englishNumbers);

  persianValue.value = parseRialToPersianText(englishNumbers);
}

watch(amountValue, (newValue) => {
  if (newValue && !!error.value) {
    validate();
    form?.validate();
  }

  emit('update:modelValue', removeCommas(toEnNumber(amountValue.value)));
});

function onFocusOut() {
  focused.value = false;
}

function validate(): any {
  if (amountType.value === 'FIX' && Number(placeholder.value) >= MIN_AMOUNT) return { valid: true, error: null };
  const amount = +extractNumberFromString(amountValue.value);
  const characterValid = !testRegexOnlyNumbers(amountValue.value);
  const minimumValid = amount >= MIN_AMOUNT;
  const maxValid = amount <= MAX_AMOUNT;
  const valid = !!amountValue.value && minimumValid && characterValid && maxValid;

  if (!characterValid) {
    hasError.value = true;
    error.value = 'فیلد مبلغ نمیتواند شامل کاراکترهای غیرعددی باشد';
  } else if (!minimumValid) {
    hasError.value = true;
    error.value = `مبلغ باید حداقل ${seprateCommas(MIN_AMOUNT)} ریال باشد.`;
  } else if (!maxValid) {
    hasError.value = true;
    error.value = `مبلغ باید حداکثر ${seprateCommas(MAX_AMOUNT)} ریال باشد.`;
  } else {
    hasError.value = false;
    error.value = '';
  }

  return { valid, error: error.value };
}

async function onFocus() {
  if (disabled.value) return;
  focused.value = true;
  await nextTick();
  input.value.focus();
}

function onBlur() {
  if (!amountValue.value) {
    focused.value = false;
  }
}
</script>

<style lang="scss" scoped>
@import './index.scss';
</style>
